import { forwardRef, useState } from 'react';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';

// ═══════════════════════════════════════════
//  INPUT — premium with soft inset shadow
// ═══════════════════════════════════════════
export const Input = forwardRef(
  (
    {
      label,
      error,
      helper,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      size = 'md',
      className = '',
      containerClassName = '',
      required = false,
      type = 'text',
      ...props
    },
    ref,
  ) => {
    const [showPw, setShowPw] = useState(false);
    const isPassword = type === 'password';
    const actualType = isPassword ? (showPw ? 'text' : 'password') : type;

    const sizeStyles = {
      sm: 'py-1.5 text-[12px]',
      md: 'py-2.5 text-[13px]',
      lg: 'py-3 text-sm',
    };

    return (
      <div className={containerClassName}>
        {label && (
          <label className="label-base">
            {label}
            {required && <span className="text-danger-500 ml-0.5">*</span>}
          </label>
        )}
        <div className="relative group">
          {LeftIcon && (
            <LeftIcon
              size={size === 'sm' ? 13 : 14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500 group-focus-within:text-primary-900 transition-colors pointer-events-none"
            />
          )}
          <input
            ref={ref}
            type={actualType}
            className={`
              input-base ${sizeStyles[size]}
              ${LeftIcon ? 'pl-9' : ''}
              ${RightIcon || isPassword ? 'pr-9' : ''}
              ${error ? 'input-error' : ''}
              ${props.disabled ? 'input-disabled' : ''}
              ${className}
            `}
            aria-invalid={!!error}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 hover:text-surface-600 transition-colors"
            >
              {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          )}
          {RightIcon && !isPassword && (
            <RightIcon
              size={14}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 pointer-events-none"
            />
          )}
        </div>
        {error && (
          <p className="error-text">
            <AlertCircle size={11} />
            {error}
          </p>
        )}
        {helper && !error && <p className="helper-text">{helper}</p>}
      </div>
    );
  },
);
Input.displayName = 'Input';

// ═══════════════════════════════════════════
//  TEXTAREA
// ═══════════════════════════════════════════
export const TextArea = forwardRef(
  (
    {
      label,
      error,
      helper,
      rows = 3,
      resize = 'vertical',
      className = '',
      containerClassName = '',
      required = false,
      ...props
    },
    ref,
  ) => {
    const resizeClass = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    return (
      <div className={containerClassName}>
        {label && (
          <label className="label-base">
            {label}
            {required && <span className="text-danger-500 ml-0.5">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          rows={rows}
          className={`
            input-base leading-relaxed
            ${resizeClass[resize]}
            ${error ? 'input-error' : ''}
            ${props.disabled ? 'input-disabled' : ''}
            ${className}
          `}
          aria-invalid={!!error}
          {...props}
        />
        {error && (
          <p className="error-text">
            <AlertCircle size={11} />
            {error}
          </p>
        )}
        {helper && !error && <p className="helper-text">{helper}</p>}
      </div>
    );
  },
);
TextArea.displayName = 'TextArea';

// ═══════════════════════════════════════════
//  SELECT
// ═══════════════════════════════════════════
export const Select = forwardRef(
  (
    {
      label,
      error,
      helper,
      options = [],
      placeholder = 'Select...',
      size = 'md',
      className = '',
      containerClassName = '',
      required = false,
      ...props
    },
    ref,
  ) => {
    const sizeStyles = {
      sm: 'py-1.5 text-[12px]',
      md: 'py-2.5 text-[13px]',
      lg: 'py-3 text-sm',
    };

    return (
      <div className={containerClassName}>
        {label && (
          <label className="label-base">
            {label}
            {required && <span className="text-danger-500 ml-0.5">*</span>}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            className={`
              input-base ${sizeStyles[size]} pr-9 appearance-none cursor-pointer
              ${error ? 'input-error' : ''}
              ${props.disabled ? 'input-disabled' : ''}
              ${className}
            `}
            aria-invalid={!!error}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((opt) => {
              const o = typeof opt === 'object' ? opt : { value: opt, label: String(opt) };
              return (
                <option key={o.value} value={o.value} disabled={o.disabled}>
                  {o.label}
                </option>
              );
            })}
          </select>
          <svg
            className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 pointer-events-none"
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        {error && (
          <p className="error-text">
            <AlertCircle size={11} />
            {error}
          </p>
        )}
        {helper && !error && <p className="helper-text">{helper}</p>}
      </div>
    );
  },
);
Select.displayName = 'Select';

// ═══════════════════════════════════════════
//  CHECKBOX — gradient checked state
// ═══════════════════════════════════════════
export const Checkbox = forwardRef(
  ({ label, error, className = '', containerClassName = '', ...props }, ref) => (
    <div className={containerClassName}>
      <label className={`inline-flex items-center gap-2.5 cursor-pointer select-none ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <div className="relative flex items-center justify-center">
          <input
            ref={ref}
            type="checkbox"
            className={`
              peer w-[18px] h-[18px] rounded-[6px] border-[1.5px] border-surface-300 bg-white
              checked:bg-primary-900 checked:border-primary-900
              focus-visible:ring-2 focus-visible:ring-primary-400/20
              transition-all duration-200 cursor-pointer appearance-none
              ${className}
            `}
            {...props}
          />
          <svg
            className="absolute w-2.5 h-2.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"
            strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        {label && <span className="text-[13px] text-surface-700">{label}</span>}
      </label>
      {error && <p className="error-text mt-1">{error}</p>}
    </div>
  ),
);
Checkbox.displayName = 'Checkbox';

// ═══════════════════════════════════════════
//  RADIO
// ═══════════════════════════════════════════
export const Radio = forwardRef(
  ({ label, className = '', containerClassName = '', ...props }, ref) => (
    <div className={containerClassName}>
      <label className={`inline-flex items-center gap-2.5 cursor-pointer select-none ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <input
          ref={ref}
          type="radio"
          className={`
            w-[18px] h-[18px] rounded-full border-[1.5px] border-surface-300 bg-white
            checked:border-primary-900 checked:border-[5px]
            focus-visible:ring-2 focus-visible:ring-primary-400/20
            transition-all duration-200 cursor-pointer appearance-none
            ${className}
          `}
          {...props}
        />
        {label && <span className="text-[13px] text-surface-700">{label}</span>}
      </label>
    </div>
  ),
);
Radio.displayName = 'Radio';

// ═══════════════════════════════════════════
//  SWITCH — gradient track
// ═══════════════════════════════════════════
export const Switch = forwardRef(
  (
    {
      label,
      size = 'md',
      className = '',
      containerClassName = '',
      ...props
    },
    ref,
  ) => {
    const sizes = {
      sm: { track: 'w-7 h-4', thumb: 'w-3 h-3', translate: 'translate-x-3' },
      md: { track: 'w-9 h-5', thumb: 'w-3.5 h-3.5', translate: 'translate-x-4' },
      lg: { track: 'w-11 h-6', thumb: 'w-4.5 h-4.5', translate: 'translate-x-[20px]' },
    };
    const s = sizes[size];

    return (
      <div className={containerClassName}>
        <label className={`inline-flex items-center gap-2.5 cursor-pointer select-none ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <div className="relative">
            <input
              ref={ref}
              type="checkbox"
              className="peer sr-only"
              {...props}
            />
            <div
              className={`
                ${s.track} rounded-full transition-all duration-250
                bg-surface-300 peer-checked:bg-primary-900
                peer-focus-visible:ring-2 peer-focus-visible:ring-primary-400/20 peer-focus-visible:ring-offset-2
              `}
            />
            <div
              className={`
                absolute top-[3px] left-[3px] ${s.thumb}
                bg-white rounded-full shadow-sm
                transition-transform duration-250 ease-spring
                peer-checked:${s.translate}
              `}
            />
          </div>
          {label && <span className="text-[13px] text-surface-700">{label}</span>}
        </label>
      </div>
    );
  },
);
Switch.displayName = 'Switch';
